import React, { useState, useEffect, useMemo } from 'react';
import { withStyles } from '@material-ui/core/styles';
import sortBy from 'lodash/sortBy';
import debounce from 'lodash/debounce';
import { useQuery } from 'react-query';
import * as API from 'api';

import ENUMS from '../../constants/ENUMS.gen.json';

import MuiTypography from '@material-ui/core/Typography';
import MuiPaper from '@material-ui/core/Paper';
import MuiTextField from '@material-ui/core/TextField';
import MuiAutoComplete from '@material-ui/lab/Autocomplete';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import MuiFormHelperText from '@material-ui/core/FormHelperText';
import MuiGrid from '@material-ui/core/Grid';
import MuiInputAdornment from '@material-ui/core/InputAdornment';
import MuiSearchIcon from '@material-ui/icons/Search';
import ViewWrapper from 'views/ViewWrapper';
import TableSplits from 'components/TableSplits/table-splits';
import ReleaseMenuItem from './ReleaseMenuItem';

const styles = theme => ({
  paper: {
    padding: theme.spacing(2),
  },
  select: {
    minWidth: '100px',
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
  actionRow: {
    paddingBottom: theme.spacing(1),
    height: '5rem',
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  formRow: {
    display: 'flex',
  },
});

const TrackSplits = ({ ...props }) => {
  const { classes } = props;

  const Releases = API.Releases();
  const Parties = API.Parties();
  const Tracks = API.Tracks();

  const [selectedParty, setSelectedParty] = useState(null);
  const [filteredReleases, setFilteredReleases] = useState([]);
  const [selectedReleaseId, setSelectedReleaseId] = useState('');
  const [trackData, setTrackData] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');

  // Queries
  //TECH DEBT:
  //This query is segmented, but it already has 800+ artists. We should consider having a 'db search first' approach to futher limit instead of
  //loading all and doing a Browser Search.
  const { data: artists, isSuccess } = useQuery(
    'getArtistParties',
    () => Parties.getPaginatedArtistList({ type: ENUMS.PARTY_TYPE.ARTIST }),
    {
      refetchOnWindowFocus: false,
    }
  );

  const partyWithReleasesData = useQuery('getPartyWithReleaseData', () => Parties.getWithReleases(selectedParty.id), {
    enabled: false,
    onSuccess: res => setFilteredReleases(res.releases),
  });

  const releaseTrackData = useQuery(
    ['getReleaseTrackData', selectedReleaseId],
    () => Releases.tracks(selectedReleaseId),
    {
      enabled: false,
      onSuccess: res => setTrackData(res),
    }
  );

  // Effects
  useEffect(() => {
    if (selectedReleaseId && selectedReleaseId !== '') {
      releaseTrackData.refetch();
    }
  }, [selectedReleaseId]);

  useEffect(() => {
    setSelectedReleaseId('');
    setFilteredReleases([]);

    if (selectedParty) {
      setSearchTerm('');
      partyWithReleasesData.refetch();
    }
  }, [selectedParty]);

  useEffect(() => {
    if (searchTerm === '') return;
    debouncedSearch(searchTerm);
  }, [searchTerm]);

  // Handlers
  const handleSelectParty = (option, value) => {
    if (value) {
      setSelectedParty(value);
    }
    return option === value;
  };

  const handleReleaseChange = e => {
    const releaseId = e.target.value;
    setSelectedReleaseId(releaseId);
  };

  const fetchTracksByUpc = async upc => {
    const releaseData = await Releases.getAllWithQuery({ params: { upc } });
    if (releaseData.total >= 1) setSelectedReleaseId(releaseData.results[0].asset_release_id);
  };

  const fetchTracksByIsrc = async isrc => {
    const tracks = await Tracks.searchByIsrc(isrc);
    setTrackData(tracks?.results);
  };

  // Search

  const debouncedSearchHandler = value => {
    document.getElementsByClassName('MuiAutocomplete-clearIndicator')[0].click();
    setTrackData([]);
    setSelectedReleaseId('');
    setSelectedParty(null);

    if (value.match(/^[0-9]+$/)) {
      fetchTracksByUpc(value);
    } else {
      fetchTracksByIsrc(value);
    }
  };

  const debouncedSearch = useMemo(() => debounce(debouncedSearchHandler, 350), []);

  const handleSearch = ({ target: { value } }) => {
    debouncedSearch(value);
    setSearchTerm(value);
  };

  return (
    <ViewWrapper>
      <MuiGrid item xs={12}>
        <MuiPaper className={classes.paper}>
          <MuiTypography className={classes.title} variant="h5">
            Track Splits
          </MuiTypography>
          <div
            className={classes.actionRow}
            style={{ marginBottom: '4rem', display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <MuiAutoComplete
                disablePortal
                disabled={!isSuccess}
                autoSelect
                PaperComponent={({ children }) => <MuiPaper style={{ marginTop: '3.5rem' }}>{children}</MuiPaper>}
                options={sortBy(artists, ['full_name'])}
                style={{
                  width: 350,
                  marginRight: '4rem',
                  marginTop: '0.3rem',
                  alignItems: 'flex-start',
                }}
                getOptionLabel={option => `${option.full_name}`}
                getOptionSelected={handleSelectParty}
                renderInput={params => <MuiTextField {...params} label={'Filter by artist'} />}
              />
              <MuiFormControl style={{ marginTop: '0.5rem' }}>
                <MuiFormHelperText>
                  {partyWithReleasesData.isFetching
                    ? '...'
                    : selectedParty && !filteredReleases.length
                      ? 'No releases found'
                      : 'Select Release'}
                </MuiFormHelperText>
                <MuiSelect
                  disabled={!selectedParty || !filteredReleases?.length}
                  default="none"
                  value={selectedReleaseId}
                  onChange={handleReleaseChange}
                  style={{ width: '22rem' }}>
                  {!selectedParty
                    ? []
                    : sortBy(filteredReleases)
                      .filter(r => r.title.trim().length)
                      .map((release, i) => (
                        <MuiMenuItem key={i} value={release.id}>
                          <ReleaseMenuItem release={release} />
                        </MuiMenuItem>
                      ))}
                </MuiSelect>
              </MuiFormControl>
            </div>
            <MuiTextField
              variant="outlined"
              label="Search by UPC or ISRC"
              placeholder="UPC or ISRC"
              onChange={handleSearch}
              value={searchTerm}
              InputProps={{
                startAdornment: (
                  <MuiInputAdornment position="start">
                    <MuiSearchIcon />
                  </MuiInputAdornment>
                ),
              }}
            />
          </div>
          <TableSplits trackData={trackData} />
        </MuiPaper>
      </MuiGrid>
    </ViewWrapper>
  );
};

export default withStyles(styles)(TrackSplits);
